/**
 * Pick the best point-cloud scan for a given tag point.
 *
 * DESIGN PRINCIPLE — spatial evidence outranks naming convention.
 *
 * The original cascade was ordered level → project → nearest-individual, which
 * put the two name-matching (location-blind) tiers ahead of the only tier that
 * consulted `tagCenter`. Measured live against production with 142 clouds and
 * 8 projects, three tag positions 20,000 units apart all returned the SAME
 * scan — the project tier answered first every time with `projects[0]`, i.e.
 * whichever project the API happened to list first. `tagCenter` had zero
 * influence on the result.
 *
 * The order below is therefore: prove it spatially if you can, and only fall
 * back to names when there is no geometry to reason from.
 *
 *   1. Level containment      — tag's Z inside a level's elevation range
 *   2. Project panorama scope — tag nearest a project's own 360 camera positions
 *   3. Name match (fallback)  — no spatial data; room-token gated, else alphabetical
 *   4. Nearest individual     — single-setup scan, last resort
 *
 * Tiers 1-3 are all COMBINED scans (project/level scope), which give 360°
 * multi-side coverage so deviation is visible from any angle. Tier 4 is a
 * single-viewpoint scan and risks occlusion shadows — preferring combined over
 * individual is a deliberate design decision (AVA-337) and is preserved.
 *
 * Combined-vs-individual classifier: naming convention, not a boolean.
 *   - Project-level scan: `pointCloud.name === project.name`
 *   - Level scan:         `pointCloud.name === level.name`
 *   - Individual scan:    `pointCloud.name === panoramaImage.name`
 *
 * TWO DATA HAZARDS THIS GUARDS AGAINST (both observed live):
 *   - **Duplicate names.** `1HDC0607ZL` exists 4x and `1HDC0607ZL-001` 3x. A
 *     bare `.find()` returns whichever the API listed first, so the same query
 *     could resolve to a superseded upload. `dedupeByName` keeps the newest.
 *   - **Superseded revisions.** Projects include explicit `(old)` variants
 *     (`1HDC0607ZL (old)`). Auto-selection must never land on one; a surveyor
 *     can still choose it manually.
 *
 * `selectedLevel` is deliberately NOT used as a fallback. It reflects whichever
 * level the user last looked at, which says nothing about where this tag is —
 * using it silently returns a confidently wrong scan.
 *
 * LIMITS — READ BEFORE TRUSTING THE RESULT
 * `state.pointClouds[]` carries no spatial metadata at all (keys are
 * createDate / createdBy / forgeAsset / id / isOnlyURL / isSelected / isShown /
 * isUploaded / name / project / projectId / url). True bbox containment is
 * impossible client-side without downloading each octree's metadata. Tiers 1-2
 * approximate scope from levels and panorama positions; where neither exists
 * the result is a NAME match and nothing more. That is why the resolved scan
 * name is surfaced in ViewerStatusBar — the surveyor must be able to see, and
 * override, what their readings are being measured against.
 *
 * Pure function. Returns the matching pointCloud entry or null.
 *
 * EVERYTHING HERE IS COMPARED IN SITE METRES, and the caller owes it a point in
 * that space. Not a style choice — it is the only space this file has any data
 * in. Every spatial signal it reads arrives from the API already stated in
 * metres: level `bottomElevation`/`topElevation` (`29..33.299` on Merchant) and
 * panorama `forgeCameraPosition` (`x 5.5, y 15.4, z 31`). The Forge model those
 * scans cover is a different matter — on Merchant it is authored in MILLIMETRES.
 * Handing this function a raw viewer coordinate therefore compares a number near
 * 25000 against elevations near 30: no level contains the tag, no camera is
 * near it, and the cascade returns a confident wrong answer or nothing at all.
 * `GET_AUTO_SCAN` converts with `viewerToSite` before calling in; see
 * `utils/modelUnits.js`, which names this as one of its three conversion leaves.
 *
 * @param {{ x: number, y: number, z: number }} tagCentreSite
 *   Where the verified tag is, in SITE METRES.
 * @param {object} storeState
 *   Subset of the Vuex store state. Required keys:
 *   - `pointClouds: Array<{ name, url, isShown, createDate, projectId }>`
 *   Optional keys (all used opportunistically for matching):
 *   - `levels: Array<{ name, bottomElevation?, topElevation? }>`
 *   - `projects: Array<{ id, name, isArchived?, images? }>`
 *   - `forgeImages: Array<{ name, forgeCameraPosition: { x, y, z } }>`
 * @param {string | null} [preferPrefix]
 *   The tag's own room token — `1HDD` from support `1HDD06ST4044` — where the
 *   support number carries one. Tier 3 ONLY, and a gate rather than a hint: a
 *   name-matched scan must start with it or that tier declines. Omitted, tier 3
 *   keeps its deterministic alphabetical order. The spatial tiers never read it.
 * @returns {object | null}  The chosen point cloud entry, or null if none available.
 */

/**
 * Matches an explicitly superseded revision, e.g. `1HDC0607ZL (old)`.
 * Exported so the manual scan selector applies the SAME definition — if the two
 * ever diverged, a scan auto-selection refuses to use could still be presented
 * to the surveyor as current.
 */
export const SUPERSEDED_NAME = /\s*\(old\)\s*$/i;

export function pickScanForTagPoint(tagCentreSite, storeState, preferPrefix = null) {
  if (!tagCentreSite || !storeState) return null;
  const rawClouds = Array.isArray(storeState.pointClouds) ? storeState.pointClouds : [];
  if (rawClouds.length === 0) return null;

  // One entry per name, newest upload wins — removes the duplicate ambiguity
  // before any tier gets to run.
  const pointClouds = dedupeByName(rawClouds);
  const levels = Array.isArray(storeState.levels) ? storeState.levels : [];
  const projects = (Array.isArray(storeState.projects) ? storeState.projects : []).filter(
    (p) => p?.name && !p.isArchived && !SUPERSEDED_NAME.test(p.name),
  );
  // EVERY panorama we can see, from BOTH sources.
  //
  // `forgeImages` is the root catalogue; `project.images` is the per-project
  // list. Tier 2 always read both, tier 4 read only `forgeImages` — an asymmetry
  // that is invisible on a facility populating both and fatal on one that does
  // not. Measured live on `Master Test Site / Merchant`: `forgeImages` was
  // `undefined` while project `Merchant` carried 73 images, ALL with a valid
  // `forgeCameraPosition`, and 72 of the 73 clouds had an exactly matching image
  // name. Tier 4 could have answered and instead returned null, so the cascade
  // produced no scan at all on a facility that had everything it needed.
  const panoramas = collectPanoramas(storeState, projects);

  // --- Tier 1: level containment (spatial) ---
  // The narrowest combined scope we can actually verify: the tag's elevation
  // falls inside this level's range.
  const containingLevel = findLevelContaining(tagCentreSite, levels);
  if (containingLevel?.name) {
    const levelScan = findByName(pointClouds, containingLevel.name);
    if (levelScan) return levelScan;
  }

  // --- Tier 2: project panorama scope (spatial) ---
  // A project scan's real coverage is wherever its 360 setups were taken, so
  // score each candidate by distance from the tag to that project's nearest
  // camera position. This is what makes the choice depend on `tagCentreSite`.
  const byProximity = findNearestProjectScan(tagCentreSite, pointClouds, projects, panoramas);
  if (byProximity) return byProximity;

  // --- Tier 3: name match (no spatial data available) ---
  // Sorted so the answer is deterministic rather than a function of API order.
  // Levels first (narrower scope) then projects, matching the tier intent.
  //
  // `preferPrefix` is the tag's own room token, and where one exists it is a
  // REQUIREMENT here, not a preference. Alphabetical order is arbitrary with
  // respect to correctness: on the demo facility, support `1HDD06ST4044` sorted
  // onto `1HDC0607ZL` — a scan of a DIFFERENT room, which loads a cloud with no
  // coverage of the support at all. The surveyor then clicks the viewer and
  // nothing happens, because there are no points there to pick.
  //
  // Sorting the token to the front was not enough to stop that. The loop below
  // takes the first candidate that HAS a live cloud, so a promoted `1HDD…`
  // project whose only upload is `(old)` — a real shape; see the superseded
  // hazard above — is skipped and the NEXT ROOM answers in its place. Nothing
  // about the tag changed. So the token FILTERS: with a token and no candidate
  // matching it, this tier says nothing, and the cascade falls through to the
  // nearest positioned scan or returns null. Null is a state the caller already
  // handles — the surveyor picks a scan by hand — and a wrong-room scan is not,
  // because it reads as an answer.
  //
  // With NO token derivable there is no evidence of which room the tag is in, so
  // the deterministic alphabetical order stands exactly as before.
  //
  // Either way this is a NAME heuristic and never a spatial answer. It cannot be
  // one: `state.pointClouds[]` carries no bbox, bounds, offset or transform, so a
  // containment test would need a download per candidate. The real fix is scan
  // extents on the API payload.
  const roomPrefix = typeof preferPrefix === 'string' ? preferPrefix.trim().toUpperCase() : '';
  const namedCandidates = [
    ...levels.filter((l) => l?.name && !SUPERSEDED_NAME.test(l.name)).map((l) => l.name),
    ...projects.map((p) => p.name),
  ]
    .filter((name) => !roomPrefix || name.toUpperCase().startsWith(roomPrefix))
    .sort((a, b) => a.localeCompare(b));
  for (const name of namedCandidates) {
    const match = findByName(pointClouds, name);
    if (match) return match;
  }

  // --- Tier 4: nearest individual scan (last resort, spatial) ---
  // Single-viewpoint coverage — occlusion shadows are possible, so this only
  // runs when no combined scan could be identified at all.
  return findNearestIndividualScan(tagCentreSite, pointClouds, panoramas);
}

/**
 * Every panorama reachable from the store, from both catalogues, deduplicated.
 *
 * DEDUPLICATED ON PURPOSE. `projectCameraPositions` unions a project's own
 * images with the root ones that name it, so once the root list also contains
 * the project images the same camera would be counted twice. That does not
 * change a minimum-distance result today, but it makes any future scoring that
 * averages or counts positions quietly wrong.
 *
 * Identity is `id` when present, falling back to `name` — a panorama with
 * neither cannot be matched to a scan anyway, so it is kept only if it is the
 * first of its kind and never suppresses a real one.
 */
function collectPanoramas(storeState, projects) {
  const root = Array.isArray(storeState?.forgeImages) ? storeState.forgeImages : [];
  const owned = (Array.isArray(projects) ? projects : []).flatMap((p) =>
    Array.isArray(p?.images) ? p.images : [],
  );

  const seen = new Set();
  const out = [];
  for (const img of [...root, ...owned]) {
    if (!img) continue;
    const key = img.id || img.name;
    if (key) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    out.push(img);
  }
  return out;
}

/**
 * Collapse same-named clouds to a single newest entry.
 * `1HDC0607ZL` exists four times in production; without this the caller gets
 * whichever the API happened to list first, which may be a superseded upload.
 * Explicitly-superseded names are dropped entirely from auto-selection.
 */
function dedupeByName(pointClouds) {
  const newestByName = new Map();
  for (const pc of pointClouds) {
    if (!pc?.name || SUPERSEDED_NAME.test(pc.name)) continue;
    const existing = newestByName.get(pc.name);
    if (!existing || uploadedAt(pc) > uploadedAt(existing)) newestByName.set(pc.name, pc);
  }
  return Array.from(newestByName.values());
}

/** Upload time as a comparable number; unparseable dates sort oldest. */
export function uploadedAt(pointCloud) {
  const t = Date.parse(pointCloud?.createDate);
  return Number.isNaN(t) ? -Infinity : t;
}

function findByName(pointClouds, name) {
  return pointClouds.find((pc) => pc.name === name) || null;
}

function distanceSq(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
}

/**
 * Find the level whose elevation range contains the tag point (Z axis).
 * Returns null if no level has bounds or no level contains the point.
 */
function findLevelContaining(point, levels) {
  if (!point || !Number.isFinite(point.z) || !Array.isArray(levels)) return null;
  for (const level of levels) {
    const bottom = level?.bottomElevation;
    const top = level?.topElevation;
    if (!Number.isFinite(bottom) || !Number.isFinite(top)) continue;
    if (point.z >= bottom && point.z <= top) return level;
  }
  return null;
}

/**
 * Among project-named (combined) scans, pick the one whose project has a 360
 * setup closest to the tag.
 *
 * Approximates each project's coverage by its own panorama camera positions —
 * the only spatial signal available client-side, since point-cloud entries
 * carry no extents. Projects with no positioned panoramas are skipped rather
 * than guessed at, so this returns null when there is nothing to reason from
 * and the caller falls through to the name-match tier.
 */
function findNearestProjectScan(tagCentreSite, pointClouds, projects, panoramas) {
  let best = null;
  let bestDistSq = Infinity;

  for (const project of projects) {
    const scan = findByName(pointClouds, project.name);
    if (!scan) continue;

    for (const position of projectCameraPositions(project, panoramas)) {
      const d = distanceSq(position, tagCentreSite);
      if (d < bestDistSq) {
        bestDistSq = d;
        best = scan;
      }
    }
  }
  return best;
}

/**
 * Camera positions belonging to a project. Prefers the project's own `images`
 * collection and falls back to matching the flat `forgeImages` list by
 * `projectId`, since the two are populated by different code paths.
 */
function projectCameraPositions(project, panoramas) {
  const own = Array.isArray(project?.images) ? project.images : [];
  const linked = panoramas.filter((img) => img?.projectId && img.projectId === project?.id);
  return [...own, ...linked]
    .map((img) => img?.forgeCameraPosition)
    .filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z));
}

/**
 * Nearest single-setup scan, matched to a panorama by name. Last resort — a
 * single viewpoint can leave the verified feature in an occlusion shadow.
 */
function findNearestIndividualScan(tagCentreSite, pointClouds, panoramas) {
  if (panoramas.length === 0) return null;

  let nearest = null;
  let minDistSq = Infinity;
  for (const scan of pointClouds) {
    const panorama = panoramas.find((img) => img?.name === scan.name);
    const pos = panorama?.forgeCameraPosition;
    if (!pos || !Number.isFinite(pos.x)) continue;
    const distSq = distanceSq(pos, tagCentreSite);
    if (distSq < minDistSq) {
      minDistSq = distSq;
      nearest = scan;
    }
  }
  return nearest;
}

export default pickScanForTagPoint;
